import {
  Add,
  Close,
  Delete,
  Edit,
  PowerSettingsNew,
} from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  addPaymentConfiguration,
  addPaymentConfigRules,
  addProduct,
  deactivateRule,
  editPaymentConfiguration,
  editPaymentConfigRule,
  editProduct,
  fetchAllPaymentConfigRules,
  fetchAllPaymentConfigurations,
  fetchAllProducts,
  fetchMappingsByProduct,
  removeAllMappings,
  removeSingleMapping,
  removePaymentConfigRule,
  removePaymentConfiguration,
  removeProduct,
  saveMappings,
  selectMappings,
  selectPaymentConfigRules,
  selectPaymentConfigurations,
  selectPlatformLoading,
  selectProducts,
  selectWizard,
  setWizardConfigurationId,
  setWizardProductId,
} from "../../../../Redux/Slices/platformSlice";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../commons/Toast/ErrorMessage";
import {
  getConsumersByCompId,
  getGeneratorsByCompId,
} from "../../../../Services/Admin/Platform";
import {
  getFullConsumerDetails,
  getFullGeneratorDetails,
} from "../../../../Services/GNA/GetData";
import {
  getAllConsumerDetailsSuperAdmin,
  getAllGeneratorDetailsSuperAdmin,
} from "../../../../Services/GNA/AlterApi";
import Styles from "./ConfigurePlatform.module.css";
import { fetchCompany } from "../../../../Redux/Slices/gnaAdminSlice";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import Loader from "../../../../commons/Loader/Loader";

// ─── Constants ────────────────────────────────────────────────────────────────
const PRODUCT_TYPES = ["RA", "RFP_RFQ", "BILATERAL", "TRADE_CONNECT"];

// ─── Initial Form States ──────────────────────────────────────────────────────
const PRODUCT_FORM_INIT = { productType: "RA", comment: "" };
const CONFIG_FORM_INIT = { configurationName: "", comment: "" };
const RULE_FORM_INIT = {
  listingType: "",
  paymentConfigurationId: "",
  minScheduleDays: 0,
  maxScheduleDays: 0,
  scheduleDayFrom: 0,
  scheduleDayTo: 0,
  relativeDayFrom: "",
  relativeDayTo: "",
  ruleType: "",
  triggerOffsetDays: 0,
  triggerReferenceType: "",
  percentage: 0,
  percentageSource: "",
  paymentType: "",
  transactionType: "",
  slidingStartDay: 0,
  version: 0,
  scopeType: "",
  scopeId: 0,
  entityType: "",
};

// ─── Reusable Field Components ────────────────────────────────────────────────
const FormField = ({ label, name, value, onChange, type = "text" }) => (
  <div className={Styles.field}>
    <label className={Styles.fieldLabel}>{label}</label>
    <input
      className={Styles.fieldInput}
      name={name}
      value={value}
      onChange={onChange}
      type={type}
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options }) => (
  <div className={Styles.field}>
    <label className={Styles.fieldLabel}>{label}</label>
    <select
      className={Styles.fieldSelect}
      name={name}
      value={value}
      onChange={onChange}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  </div>
);

const DataSelect = ({
  label,
  name,
  value,
  onChange,
  items,
  getValue,
  getLabel,
  onClick,
}) => (
  <div className={Styles.field}>
    <label className={Styles.fieldLabel}>{label}</label>
    <select
      className={Styles.fieldSelect}
      name={name}
      value={value}
      onChange={onChange}
      onClick={onClick}
    >
      <option value="">Select...</option>
      {items.map((item) => (
        <option key={getValue(item)} value={getValue(item)}>
          {getLabel(item)}
        </option>
      ))}
    </select>
  </div>
);

// ─── Chip ─────────────────────────────────────────────────────────────────────
const Chip = ({ label, variant = "teal" }) => (
  <span className={`${Styles.chip} ${Styles[`chip_${variant}`]}`}>{label}</span>
);

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = () => (
  <div className={Styles.loaderBox}>
    <div className={Styles.spinner} />
  </div>
);

// ─── Form Dialog ──────────────────────────────────────────────────────────────
const FormDialog = ({
  open,
  onClose,
  title,
  onSubmit,
  submitLabel,
  children,
}) => {
  if (!open) return null;
  return (
    <div className={Styles.dialogOverlay} onClick={onClose}>
      <div className={Styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={Styles.dialogHeader}>
          <span className={Styles.dialogTitle}>{title}</span>
          <button className={Styles.dialogClose} onClick={onClose}>
            <Close fontSize="small" />
          </button>
        </div>
        <div className={Styles.dialogBody}>{children}</div>
        <div className={Styles.dialogFooter}>
          <button className={Styles.btnOutline} onClick={onClose}>
            Cancel
          </button>
          <button className={Styles.btnPrimary} onClick={onSubmit}>
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Data Table ───────────────────────────────────────────────────────────────
const DataTable = ({ columns, rows, renderRow }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  return (
    <div className={Styles.tableWrapper}>
      <table className={Styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col} className={Styles.th}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {safeRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={Styles.emptyCell}>
                No records found
              </td>
            </tr>
          ) : (
            safeRows.map((row, i) => (
              <tr key={row.id ?? i} className={Styles.tr}>
                {renderRow(row)}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// ─── Products Tab ─────────────────────────────────────────────────────────────
const ProductsTab = ({ products, dispatch, wizard }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(PRODUCT_FORM_INIT);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const openAdd = () => {
    setEditId(null);
    setForm(PRODUCT_FORM_INIT);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({ productType: row.productType, comment: row.comment || "" });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const action = editId
      ? dispatch(editProduct({ ...form, id: editId }))
      : dispatch(addProduct(form));
    action
      .unwrap()
      .then(() =>
        SuccessMessage(editId ? "Product updated" : "Product created"),
      )
      .catch((err) => ErrorMessage(err || "Failed to save product"));
    setDialogOpen(false);
  };

  return (
    <div>
      <div className={Styles.sectionHeader}>
        <span className={Styles.sectionTitle}>Products</span>
        <button className={Styles.btnPrimary} onClick={openAdd}>
          <Add style={{ fontSize: 16 }} /> Add Product
        </button>
      </div>

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? "Edit Product" : "Add Product"}
        onSubmit={handleSubmit}
        submitLabel={editId ? "Update" : "Create"}
      >
        <div className={Styles.formGrid}>
          <SelectField
            label="Product Type"
            name="productType"
            value={form.productType}
            onChange={handleChange}
            options={PRODUCT_TYPES}
          />
          <FormField
            label="Comment"
            name="comment"
            value={form.comment}
            onChange={handleChange}
          />
        </div>
      </FormDialog>

      <DataTable
        columns={["ID", "Product Type", "Comment", "Actions"]}
        rows={products}
        renderRow={(row) => (
          <>
            <td className={Styles.td}>{row.id}</td>
            <td className={Styles.td}>
              <Chip label={row.productType} variant="teal" />
            </td>
            <td className={Styles.td}>{row.comment || "—"}</td>
            <td className={Styles.td}>
              <button
                className={
                  wizard.productId === row.id
                    ? Styles.btnSelectActive
                    : Styles.btnSelectInactive
                }
                onClick={() => dispatch(setWizardProductId(row.id))}
              >
                {wizard.productId === row.id ? "Selected" : "Select"}
              </button>
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconTeal}`}
                onClick={() => openEdit(row)}
                title="Edit"
              >
                <Edit fontSize="small" />
              </button>
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconRed}`}
                onClick={() =>
                  dispatch(removeProduct(row.id))
                    .unwrap()
                    .then(() => SuccessMessage("Product deleted"))
                    .catch((err) =>
                      ErrorMessage(err || "Failed to delete product"),
                    )
                }
                title="Delete"
              >
                <Delete fontSize="small" />
              </button>
            </td>
          </>
        )}
      />
    </div>
  );
};

// ─── Payment Configurations Tab ───────────────────────────────────────────────
const PaymentConfigsTab = ({ configurations, dispatch, wizard }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(CONFIG_FORM_INIT);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitized =
      name === "configurationName" ? value.replace(/[^A-Z0-9]/g, "") : value;
    setForm((f) => ({ ...f, [name]: sanitized }));
  };
  const openAdd = () => {
    setEditId(null);
    setForm(CONFIG_FORM_INIT);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      configurationName: row.configurationName,
      comment: row.comment || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const action = editId
      ? dispatch(editPaymentConfiguration({ ...form, id: editId }))
      : dispatch(addPaymentConfiguration(form));
    action
      .unwrap()
      .then(() =>
        SuccessMessage(
          editId ? "Configuration updated" : "Configuration created",
        ),
      )
      .catch((err) => ErrorMessage(err || "Failed to save configuration"));
    setDialogOpen(false);
  };

  return (
    <div>
      <div className={Styles.sectionHeader}>
        <span className={Styles.sectionTitle}>Payment Configurations</span>
        <button className={Styles.btnPrimary} onClick={openAdd}>
          <Add style={{ fontSize: 16 }} /> Add Configuration
        </button>
      </div>

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? "Edit Configuration" : "Add Configuration"}
        onSubmit={handleSubmit}
        submitLabel={editId ? "Update" : "Create"}
      >
        <div className={Styles.formGrid}>
          <FormField
            label="Configuration Name"
            name="configurationName"
            value={form.configurationName}
            onChange={handleChange}
          />
          <FormField
            label="Comment"
            name="comment"
            value={form.comment}
            onChange={handleChange}
          />
        </div>
      </FormDialog>

      <DataTable
        columns={["ID", "Configuration Name", "Comment", "Actions"]}
        rows={configurations}
        renderRow={(row) => (
          <>
            <td className={Styles.td}>{row.id}</td>
            <td className={Styles.td}>
              <Chip label={row.configurationName} variant="teal" />
            </td>
            <td className={Styles.td}>{row.comment || "—"}</td>
            <td className={Styles.td}>
              <button
                className={
                  wizard.configurationId === row.id
                    ? Styles.btnSelectActive
                    : Styles.btnSelectInactive
                }
                onClick={() => {
                  if (wizard.configurationId === row.id) {
                    dispatch(setWizardConfigurationId(null));
                  } else {
                    dispatch(setWizardConfigurationId(row.id));
                  }
                }}
              >
                {wizard.configurationId === row.id ? "Deselect" : "Select"}
              </button>
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconTeal}`}
                onClick={() => openEdit(row)}
                title="Edit"
              >
                <Edit fontSize="small" />
              </button>
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconRed}`}
                onClick={() =>
                  dispatch(removePaymentConfiguration(row.id))
                    .unwrap()
                    .then(() => SuccessMessage("Configuration deleted"))
                    .catch((err) =>
                      ErrorMessage(err || "Failed to delete configuration"),
                    )
                }
                title="Delete"
              >
                <Delete fontSize="small" />
              </button>
            </td>
          </>
        )}
      />
    </div>
  );
};

// ─── Payment Config Rules Tab ─────────────────────────────────────────────────
const PaymentConfigRulesTab = ({ rules, configurations, dispatch, wizard }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    ...RULE_FORM_INIT,
    paymentConfigurationId: wizard.configurationId || "",
  });

  useEffect(() => {
    if (wizard.configurationId)
      setForm((f) => ({
        ...f,
        paymentConfigurationId: wizard.configurationId,
      }));
  }, [wizard.configurationId]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const openAdd = () => {
    setEditId(null);
    setForm({
      ...RULE_FORM_INIT,
      paymentConfigurationId: wizard.configurationId || "",
    });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({ ...row });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const action = editId
      ? dispatch(editPaymentConfigRule({ ...form, id: editId }))
      : dispatch(addPaymentConfigRules([{ ...form }]));
    action
      .unwrap()
      .then(() => SuccessMessage(editId ? "Rule updated" : "Rule created"))
      .catch((err) => ErrorMessage(err || "Failed to save rule"));
    setDialogOpen(false);
    setEditId(null);
    setForm({
      ...RULE_FORM_INIT,
      paymentConfigurationId: wizard.configurationId || "",
    });
  };

  return (
    <div>
      <div className={Styles.sectionHeader}>
        <span className={Styles.sectionTitle}>Payment Config Rules</span>
        <button className={Styles.btnPrimary} onClick={openAdd}>
          <Add style={{ fontSize: 16 }} /> Add Rule
        </button>
      </div>

      {wizard.configurationId && (
        <div className={Styles.alertInfo}>
          Using Configuration <strong>#{wizard.configurationId}</strong>
        </div>
      )}

      <FormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditId(null);
        }}
        title={editId ? "Edit Config Rule" : "Add Config Rule"}
        onSubmit={handleSubmit}
        submitLabel={editId ? "Update" : "Create"}
      >
        <div className={Styles.formGrid}>
          <FormField
            label="Listing Type"
            name="listingType"
            value={form.listingType}
            onChange={handleChange}
          />
          <DataSelect
            label="Configuration"
            name="paymentConfigurationId"
            value={form.paymentConfigurationId}
            onChange={handleChange}
            items={configurations}
            getValue={(c) => c.id}
            getLabel={(c) => `${c.configurationName} (#${c.id})`}
          />
          <FormField
            label="Rule Type"
            name="ruleType"
            value={form.ruleType}
            onChange={handleChange}
          />
          <FormField
            label="Payment Type"
            name="paymentType"
            value={form.paymentType}
            onChange={handleChange}
          />
          <FormField
            label="Transaction Type"
            name="transactionType"
            value={form.transactionType}
            onChange={handleChange}
          />
          <FormField
            label="Scope Type"
            name="scopeType"
            value={form.scopeType}
            onChange={handleChange}
          />
          <FormField
            label="Entity Type"
            name="entityType"
            value={form.entityType}
            onChange={handleChange}
          />
          <FormField
            label="Relative Day From"
            name="relativeDayFrom"
            value={form.relativeDayFrom}
            onChange={handleChange}
          />
          <FormField
            label="Relative Day To"
            name="relativeDayTo"
            value={form.relativeDayTo}
            onChange={handleChange}
          />
          <FormField
            label="Trigger Reference"
            name="triggerReferenceType"
            value={form.triggerReferenceType}
            onChange={handleChange}
          />
          <FormField
            label="Percentage Source"
            name="percentageSource"
            value={form.percentageSource}
            onChange={handleChange}
          />
          <FormField
            label="Min Schedule Days"
            name="minScheduleDays"
            value={form.minScheduleDays}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Max Schedule Days"
            name="maxScheduleDays"
            value={form.maxScheduleDays}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Schedule Day From"
            name="scheduleDayFrom"
            value={form.scheduleDayFrom}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Schedule Day To"
            name="scheduleDayTo"
            value={form.scheduleDayTo}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Trigger Offset Days"
            name="triggerOffsetDays"
            value={form.triggerOffsetDays}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Percentage"
            name="percentage"
            value={form.percentage}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Sliding Start Day"
            name="slidingStartDay"
            value={form.slidingStartDay}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Scope ID"
            name="scopeId"
            value={form.scopeId}
            onChange={handleChange}
            type="number"
          />
          <FormField
            label="Version"
            name="version"
            value={form.version}
            onChange={handleChange}
            type="number"
          />
        </div>
      </FormDialog>

      <DataTable
        columns={[
          "ID",
          "Listing Type",
          "Config ID",
          "Rule Type",
          "Payment Type",
          "Actions",
        ]}
        rows={rules}
        renderRow={(row) => (
          <>
            <td className={Styles.td}>{row.id}</td>
            <td className={Styles.td}>
              <Chip label={row.listingType} variant="teal" />
            </td>
            <td className={Styles.td}>{row.paymentConfigurationId}</td>
            <td className={Styles.td}>
              <Chip label={row.ruleType} variant="outlineTeal" />
            </td>
            <td className={Styles.td}>
              <Chip label={row.paymentType} variant="orange" />
            </td>
            <td className={Styles.td}>
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconOrange}`}
                onClick={() =>
                  dispatch(deactivateRule(row.id))
                    .unwrap()
                    .then(() => SuccessMessage("Rule deactivated"))
                    .catch((err) =>
                      ErrorMessage(err || "Failed to deactivate rule"),
                    )
                }
                title="Deactivate"
              >
                <PowerSettingsNew fontSize="small" />
              </button>
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconTeal}`}
                onClick={() => openEdit(row)}
                title="Edit"
              >
                <Edit fontSize="small" />
              </button>
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconRed}`}
                onClick={() =>
                  dispatch(removePaymentConfigRule(row.id))
                    .unwrap()
                    .then(() => SuccessMessage("Rule deleted"))
                    .catch((err) =>
                      ErrorMessage(err || "Failed to delete rule"),
                    )
                }
                title="Delete"
              >
                <Delete fontSize="small" />
              </button>
            </td>
          </>
        )}
      />
    </div>
  );
};

// ─── Mappings Tab ─────────────────────────────────────────────────────────────
const MappingsTab = ({
  allMappings,
  products,
  configurations,
  companies,
  rules,
  dispatch,
}) => {
  const wizard = useSelector(selectWizard);

  // View: which product's mappings to show in the table
  const [viewProductId, setViewProductId] = useState(wizard.productId || "");

  // Dialog open state
  const [dialogOpen, setDialogOpen] = useState(false);

  // Edit dialog
  const [editRow, setEditRow] = useState(null);
  const [editConfigId, setEditConfigId] = useState("");

  // Sequential selectors inside dialog
  const [companyIds, setCompanyIds] = useState([]);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [companySelectError, setCompanySelectError] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const companyDropdownRef = useRef(null);
  const [consumers, setConsumers] = useState([]);
  const [generators, setGenerators] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [entityKeys, setEntityKeys] = useState([]); // ["C:{id}", "G:{id}", ...]
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [entitySearch, setEntitySearch] = useState("");
  const entityDropdownRef = useRef(null);
  const [selProductId, setSelProductId] = useState(wizard.productId || "");
  const [selConfigId, setSelConfigId] = useState(wizard.configurationId || "");
  const [replaceExisting, setReplaceExisting] = useState(false);

  // Accumulated list of mapping entries
  const [entries, setEntries] = useState([]);
  const fetchedCompaniesRef = useRef(new Set()); // Track fetched companies
  const abortControllerRef = useRef(null); // Cancel previous requests

  useEffect(() => {
    if (wizard.productId) {
      setViewProductId(wizard.productId);
      setSelProductId(wizard.productId);
    }
  }, [wizard.productId]);

  useEffect(() => {
    if (viewProductId) dispatch(fetchMappingsByProduct(viewProductId));
  }, [viewProductId, dispatch]);

  // Close company dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(e.target)
      ) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Close entity dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        entityDropdownRef.current &&
        !entityDropdownRef.current.contains(e.target)
      ) {
        setEntityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Load consumers + generators when selected companies change
  useEffect(() => {
    if (companyIds.length === 0) {
      setConsumers([]);
      setGenerators([]);
      fetchedCompaniesRef.current.clear();
      return;
    }

    const effectiveIds = companyIds.includes("ALL")
      ? (companies || []).map((c) => c.companyId)
      : companyIds;

    if (effectiveIds.length === 0) return;

    // Check if we've already fetched all these companies
    const allAlreadyFetched = effectiveIds.every((id) =>
      fetchedCompaniesRef.current.has(id),
    );

    if (allAlreadyFetched) {
      setLoadingEntities(false);
      return; // Skip if all companies already fetched
    }

    // Filter out already fetched companies
    const newIds = effectiveIds.filter(
      (id) => !fetchedCompaniesRef.current.has(id),
    );

    if (newIds.length === 0) return;

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoadingEntities(true);

    Promise.all(
      newIds.map((id) =>
        Promise.all([
          getConsumersByCompId(id).catch(() => ({ data: { data: [] } })),
          getGeneratorsByCompId(id).catch(() => ({ data: { data: [] } })),
        ]).then(([cRes, gRes]) => ({
          companyId: id,
          consumers: cRes?.data?.data || cRes?.data || [],
          generators: gRes?.data?.data || gRes?.data || [],
        })),
      ),
    )
      .then((results) => {
        newIds.forEach((id) => fetchedCompaniesRef.current.add(id));

        const allConsumers = results.flatMap((r) =>
          r.consumers.map((c) => ({ ...c, _companyId: r.companyId })),
        );
        const allGenerators = results.flatMap((r) =>
          r.generators.map((g) => ({ ...g, _companyId: r.companyId })),
        );

        setConsumers((prev) => [...prev, ...allConsumers]);
        setGenerators((prev) => [...prev, ...allGenerators]);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Error loading entities:", err);
        }
      })
      .finally(() => setLoadingEntities(false));

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // Only depend on companyIds, not companies
  }, [companyIds]);

  const currentMappings = allMappings[viewProductId] || [];

  // Entity name lookup map { entityId: name }
  const [entityNameMap, setEntityNameMap] = useState({});
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);

  useEffect(() => {
    if (currentMappings.length === 0) return;
    const missing = currentMappings.filter((m) => !entityNameMap[m.entityId]);
    if (missing.length === 0) return;

    // Separate missing entities by type
    const missingConsumerIds = missing
      .filter((m) => m.entityType === "CONSUMER")
      .map((m) => m.entityId);
    const missingGeneratorIds = missing
      .filter((m) => m.entityType === "GENERATOR")
      .map((m) => m.entityId);

    // Fetch consumers in batch
    if (missingConsumerIds.length > 0) {
      getAllConsumerDetailsSuperAdmin(missingConsumerIds)
        .then((res) => {
          const consumersList = res?.data?.data || [];
          const nameUpdates = {};
          if (Array.isArray(consumersList)) {
            consumersList.forEach((consumer) => {
              if (consumer.consumerId && consumer.consumerName) {
                nameUpdates[consumer.consumerId] = consumer.consumerName;
              }
            });
          } else if (consumersList.consumerName) {
            nameUpdates[consumersList.consumerId] = consumersList.consumerName;
          }
          if (Object.keys(nameUpdates).length > 0) {
            setEntityNameMap((prev) => ({ ...prev, ...nameUpdates }));
          }
        })
        .catch(() => {});
    }

    // Fetch generators in batch
    if (missingGeneratorIds.length > 0) {
      getAllGeneratorDetailsSuperAdmin(missingGeneratorIds)
        .then((res) => {
          const generatorsList = res?.data?.data || [];
          const nameUpdates = {};
          if (Array.isArray(generatorsList)) {
            generatorsList.forEach((generator) => {
              if (generator.generatorId && generator.generatorName) {
                nameUpdates[generator.generatorId] = generator.generatorName;
              }
            });
          } else if (generatorsList.generatorName) {
            nameUpdates[generatorsList.generatorId] =
              generatorsList.generatorName;
          }
          if (Object.keys(nameUpdates).length > 0) {
            setEntityNameMap((prev) => ({ ...prev, ...nameUpdates }));
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMappings]);

  // Resolve entity from key
  const allEntities = [
    ...consumers.map((c) => ({
      key: `C:${c.consumerId}`,
      id: c.consumerId,
      name: c.consumerName || String(c.consumerId),
      type: "CONSUMER",
      companyId: c._companyId,
    })),
    ...generators.map((g) => ({
      key: `G:${g.generatorId}`,
      id: g.generatorId,
      name: g.generatorName || String(g.generatorId),
      type: "GENERATOR",
      companyId: g._companyId,
    })),
  ];
  const selectedEntities = allEntities.filter((e) =>
    entityKeys.includes(e.key),
  );

  const canAdd =
    companyIds.length > 0 &&
    entityKeys.length > 0 &&
    selProductId &&
    selConfigId;

  const handleAddEntry = () => {
    if (!canAdd || selectedEntities.length === 0) return;
    setEntries((prev) => {
      const existingKeys = new Set(
        prev.map((e) => `${e.entityId}|${e.entityType}|${e.configurationId}`),
      );
      const newEntries = selectedEntities
        .filter((entity) => {
          const k = `${entity.id}|${entity.type}|${Number(selConfigId)}`;
          return !existingKeys.has(k);
        })
        .map((entity) => ({
          configurationId: Number(selConfigId),
          entityId: entity.id,
          entityType: entity.type,
          companyId: entity.companyId,
        }));
      return [...prev, ...newEntries];
    });
    setEntityKeys([]);
  };

  const removeEntry = (i) =>
    setEntries((prev) => prev.filter((_, idx) => idx !== i));

  const openDialog = () => {
    dispatch(fetchAllProducts());
    dispatch(fetchAllPaymentConfigurations());
    dispatch(fetchAllPaymentConfigRules());
    setCompanyIds([]);
    setCompanyDropdownOpen(false);
    setCompanySelectError("");
    setCompanySearch("");
    setConsumers([]);
    setGenerators([]);
    fetchedCompaniesRef.current.clear(); // Clear cache when opening
    setEntityKeys([]);
    setEntityDropdownOpen(false);
    setEntitySearch("");
    setSelProductId(wizard.productId || "");
    setSelConfigId(wizard.configurationId || "");
    setReplaceExisting(false);
    setEntries([]);
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!selProductId || entries.length === 0) return;

    let mappings = entries.map((e) => ({
      configurationId: e.configurationId,
      entityId: e.entityId,
      entityType: e.entityType,
      companyId: e.companyId,
    }));
    let payload = {
      productId: Number(selProductId),
      replaceExisting,
      mappings,
    };

    dispatch(saveMappings({ productId: selProductId, payload: payload }))
      .unwrap()
      .then(() => SuccessMessage("Mappings saved successfully"))
      .catch((err) => ErrorMessage(err || "Failed to save mappings"));
    setViewProductId(selProductId);
    setDialogOpen(false);
  };

  const handleDeleteSingle = (row) => {
    let payload = {
      configurationId: row.configurationId,
      entityId: row.entityId,
      entityType: row.entityType,
    };
    dispatch(
      removeSingleMapping({
        productId: viewProductId,
        payload: payload,
      }),
    )
      .unwrap()
      .then(() => SuccessMessage("Mapping removed"))
      .catch((err) => ErrorMessage(err || "Failed to remove mapping"));
  };

  const handleDeleteAll = () => {
    setDeleteAllConfirmOpen(true);
  };

  const handleDeleteAllConfirm = () => {
    if (!viewProductId) return;
    dispatch(removeAllMappings(viewProductId))
      .unwrap()
      .then(() => {
        SuccessMessage("All mappings cleared");
        setDeleteAllConfirmOpen(false);
      })
      .catch((err) => ErrorMessage(err || "Failed to clear mappings"));
  };

  const openEdit = (row) => {
    dispatch(fetchAllPaymentConfigurations());
    setEditRow(row);
    setEditConfigId(String(row.configurationId));
  };

  const handleEditSubmit = () => {
    if (!editRow || !editConfigId) return;
    dispatch(
      removeSingleMapping({
        productId: viewProductId,
        payload: {
          configurationId: editRow.configurationId,
          entityId: editRow.entityId,
          entityType: editRow.entityType,
        },
      }),
    )
      .unwrap()
      .then(() =>
        dispatch(
          saveMappings({
            productId: viewProductId,
            payload: {
              productId: Number(viewProductId),
              replaceExisting: false,
              mappings: [
                {
                  configurationId: Number(editConfigId),
                  entityId: editRow.entityId,
                  entityType: editRow.entityType,
                },
              ],
            },
          }),
        ).unwrap(),
      )
      .then(() => SuccessMessage("Mapping updated"))
      .catch((err) => ErrorMessage(err || "Failed to update mapping"));
    setEditRow(null);
    setEditConfigId("");
  };

  return (
    <>
      {/* Delete All Confirmation Modal */}
      <ConfirmationModal
        open={deleteAllConfirmOpen}
        handleClose={() => setDeleteAllConfirmOpen(false)}
        header="Confirm Delete All"
        data="Are you sure you want to delete all mappings for this product?"
        note="This action cannot be undone and will remove all configured mappings."
        handleSubmit={handleDeleteAllConfirm}
        loading={false}
      />
      <div>
        {/* Header */}
        <div className={Styles.sectionHeader}>
          <span className={Styles.sectionTitle}>
            Product Configuration Mappings
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            {currentMappings.length > 0 && (
              <button
                className={`${Styles.btnIcon} ${Styles.btnIconRed}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 6,
                }}
                onClick={handleDeleteAll}
                title="Delete all mappings for this product"
              >
                <Delete style={{ fontSize: 15 }} /> Delete All
              </button>
            )}
            <button className={Styles.btnPrimary} onClick={openDialog}>
              <Add style={{ fontSize: 16 }} /> Add Mappings
            </button>
          </div>
        </div>

        {/* Product filter for view */}
        <div style={{ marginBottom: 16, maxWidth: 320 }}>
          <DataSelect
            label="View Mappings for Product"
            name="viewProductId"
            value={viewProductId}
            onChange={(e) => setViewProductId(e.target.value)}
            onClick={() => dispatch(fetchAllProducts())}
            items={products}
            getValue={(p) => p.id}
            getLabel={(p) => `${p.productType} (#${p.id})`}
          />
        </div>

        {/* Dialog */}
        <FormDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title="Add Product Configuration Mappings"
          onSubmit={handleSubmit}
          submitLabel="Save Mappings"
        >
          {/* Section: Entity Selection */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "#34656d",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                1
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#34656d" }}>
                Select Entity
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                paddingLeft: 30,
              }}
            >
              <div
                className={Styles.field}
                ref={companyDropdownRef}
                style={{ position: "relative" }}
              >
                <label className={Styles.fieldLabel}>Company</label>
                {/* Trigger */}
                <div
                  className={Styles.fieldSelect}
                  onClick={() => setCompanyDropdownOpen((o) => !o)}
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    minHeight: 32,
                    userSelect: "none",
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: companyIds.length === 0 ? "#aaa" : "#333",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {companyIds.length === 0
                      ? "Select company…"
                      : companyIds.includes("ALL")
                        ? "All Companies"
                        : `${companyIds.length} selected`}
                  </span>
                  <span style={{ fontSize: 9, marginLeft: 6, flexShrink: 0 }}>
                    ▼
                  </span>
                </div>
                {/* Selected company chips */}
                {companyIds.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 4,
                      marginTop: 5,
                      maxHeight: 64,
                      overflowY: "auto",
                      paddingRight: 2,
                    }}
                  >
                    {(companyIds.includes("ALL")
                      ? (companies || []).map((c) => c.companyId)
                      : companyIds
                    ).map((cid) => {
                      const c = (companies || []).find(
                        (x) => x.companyId === cid,
                      );
                      return (
                        <span
                          key={cid}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: "#e8f4f5",
                            color: "#34656d",
                            border: "1px solid #b2d8dc",
                            borderRadius: 100,
                            fontSize: 11,
                            padding: "2px 8px",
                          }}
                        >
                          {c ? c.companyName || c.name || `#${cid}` : cid}
                          <Close
                            style={{ fontSize: 12, cursor: "pointer" }}
                            onClick={() => {
                              // If "ALL" was selected, expand to individual ids first then remove this one
                              const currentIds = companyIds.includes("ALL")
                                ? (companies || [])
                                    .map((x) => x.companyId)
                                    .filter((id) => id !== cid)
                                : companyIds.filter((id) => id !== cid);
                              const keysToRemove = new Set(
                                allEntities
                                  .filter((e) => e.companyId === cid)
                                  .map((e) => e.key),
                              );
                              setCompanyIds(currentIds);
                              setEntityKeys((prev) =>
                                prev.filter((k) => !keysToRemove.has(k)),
                              );
                              setConsumers((prev) =>
                                prev.filter((c) => c._companyId !== cid),
                              );
                              setGenerators((prev) =>
                                prev.filter((g) => g._companyId !== cid),
                              );
                              fetchedCompaniesRef.current.delete(cid);
                              setCompanySelectError("");
                            }}
                          />
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* Error message */}
                {companySelectError && (
                  <div style={{ color: "#e53935", fontSize: 11, marginTop: 4 }}>
                    {companySelectError}
                  </div>
                )}
                {/* Dropdown panel */}
                {companyDropdownOpen && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 2px)",
                      left: 0,
                      right: 0,
                      background: "#fff",
                      border: "1px solid #ccc",
                      borderRadius: 4,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      zIndex: 200,
                    }}
                  >
                    {/* Search input */}
                    <div
                      style={{
                        padding: "6px 8px",
                        borderBottom: "1px solid #eee",
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Search company…"
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: "100%",
                          border: "1px solid #ddd",
                          borderRadius: 4,
                          padding: "4px 8px",
                          fontSize: 12,
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    {/* Scrollable list */}
                    <div style={{ maxHeight: 180, overflowY: "auto" }}>
                      {/* All Companies — only show when search is empty */}
                      {!companySearch && (
                        <div
                          style={{
                            padding: "7px 10px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            cursor: "pointer",
                            background: companyIds.includes("ALL")
                              ? "#e8f4f5"
                              : "#fff",
                            borderBottom: "1px solid #f0f0f0",
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                          onClick={() => {
                            if (companyIds.includes("ALL")) {
                              setCompanyIds([]);
                            } else {
                              setCompanyIds(["ALL"]);
                            }
                            setEntityKeys([]);
                            setCompanySelectError("");
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={companyIds.includes("ALL")}
                            readOnly
                            style={{ accentColor: "#34656d" }}
                          />
                          All Companies
                        </div>
                      )}
                      {(companies || [])
                        .filter((c) =>
                          (c.companyName || c.name || "")
                            .toLowerCase()
                            .includes(companySearch.toLowerCase()),
                        )
                        .map((c) => {
                          const isSelected = companyIds.includes(c.companyId);
                          const allSelected = companyIds.includes("ALL");
                          return (
                            <div
                              key={c.id}
                              style={{
                                padding: "7px 10px",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                cursor: "pointer",
                                background:
                                  isSelected || allSelected
                                    ? "#e8f4f5"
                                    : "#fff",
                                fontSize: 12,
                              }}
                              onClick={() => {
                                if (allSelected) {
                                  // If "ALL" is selected, expand to individual ids first
                                  const expandedIds = (companies || []).map(
                                    (x) => x.companyId,
                                  );
                                  // Remove the clicked company
                                  const newIds = expandedIds.filter(
                                    (id) => id !== c.companyId,
                                  );
                                  setCompanyIds(newIds);

                                  const keysToRemove = new Set(
                                    allEntities
                                      .filter(
                                        (e) => e.companyId === c.companyId,
                                      )
                                      .map((e) => e.key),
                                  );
                                  setEntityKeys((prev) =>
                                    prev.filter((k) => !keysToRemove.has(k)),
                                  );
                                  setConsumers((prev) =>
                                    prev.filter(
                                      (con) => con._companyId !== c.companyId,
                                    ),
                                  );
                                  setGenerators((prev) =>
                                    prev.filter(
                                      (gen) => gen._companyId !== c.companyId,
                                    ),
                                  );
                                  fetchedCompaniesRef.current.delete(
                                    c.companyId,
                                  );
                                } else if (isSelected) {
                                  // Toggle: deselect if already selected
                                  setCompanyIds((prev) =>
                                    prev.filter((id) => id !== c.companyId),
                                  );
                                  const keysToRemove = new Set(
                                    allEntities
                                      .filter(
                                        (e) => e.companyId === c.companyId,
                                      )
                                      .map((e) => e.key),
                                  );
                                  setEntityKeys((prev) =>
                                    prev.filter((k) => !keysToRemove.has(k)),
                                  );
                                  setConsumers((prev) =>
                                    prev.filter(
                                      (con) => con._companyId !== c.companyId,
                                    ),
                                  );
                                  setGenerators((prev) =>
                                    prev.filter(
                                      (gen) => gen._companyId !== c.companyId,
                                    ),
                                  );
                                  fetchedCompaniesRef.current.delete(
                                    c.companyId,
                                  );
                                } else {
                                  // Add if not selected
                                  setCompanyIds((prev) => [
                                    ...prev,
                                    c.companyId,
                                  ]);
                                  setEntityKeys([]);
                                }
                                setCompanySelectError("");
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected || allSelected}
                                readOnly
                                style={{ accentColor: "#34656d" }}
                              />
                              {c.companyName || c.name || `Company #${c.id}`}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
              <div
                className={Styles.field}
                ref={entityDropdownRef}
                style={{ position: "relative" }}
              >
                <label className={Styles.fieldLabel}>Entity</label>
                {loadingEntities ? (
                  <div style={{ fontSize: 12, color: "#888", paddingTop: 10 }}>
                    Loading entities…
                  </div>
                ) : (
                  <>
                    {/* Trigger */}
                    <div
                      className={Styles.fieldSelect}
                      onClick={() => {
                        if (companyIds.length > 0)
                          setEntityDropdownOpen((o) => !o);
                      }}
                      style={{
                        cursor:
                          companyIds.length === 0 ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: 32,
                        userSelect: "none",
                        opacity: companyIds.length === 0 ? 0.5 : 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          color: entityKeys.length === 0 ? "#aaa" : "#333",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entityKeys.length === 0
                          ? "Select entity…"
                          : `${entityKeys.length} selected`}
                      </span>
                      <span
                        style={{ fontSize: 9, marginLeft: 6, flexShrink: 0 }}
                      >
                        ▼
                      </span>
                    </div>
                    {/* Selected entity chips */}
                    {entityKeys.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 4,
                          marginTop: 5,
                          maxHeight: 64,
                          overflowY: "auto",
                          paddingRight: 2,
                        }}
                      >
                        {selectedEntities.map((entity) => (
                          <span
                            key={entity.key}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background:
                                entity.type === "CONSUMER"
                                  ? "#e8f4f5"
                                  : "#fff4ee",
                              color:
                                entity.type === "CONSUMER"
                                  ? "#34656d"
                                  : "#c0572a",
                              border: `1px solid ${entity.type === "CONSUMER" ? "#b2d8dc" : "#f1c0a0"}`,
                              borderRadius: 100,
                              fontSize: 11,
                              padding: "2px 8px",
                            }}
                          >
                            {entity.name}
                            <Close
                              style={{ fontSize: 12, cursor: "pointer" }}
                              onClick={() => {
                                setEntityKeys((prev) =>
                                  prev.filter((k) => k !== entity.key),
                                );
                              }}
                            />
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Dropdown panel */}
                    {entityDropdownOpen &&
                      (() => {
                        const q = entitySearch.toLowerCase();
                        const filteredConsumers = consumers.filter((c) =>
                          (c.consumerName || String(c.consumerId))
                            .toLowerCase()
                            .includes(q),
                        );
                        const filteredGenerators = generators.filter((g) =>
                          (g.generatorName || String(g.generatorId))
                            .toLowerCase()
                            .includes(q),
                        );
                        const allConsumerKeys = filteredConsumers.map(
                          (c) => `C:${c.consumerId}`,
                        );
                        const allGeneratorKeys = filteredGenerators.map(
                          (g) => `G:${g.generatorId}`,
                        );
                        const allConsumersSelected =
                          allConsumerKeys.length > 0 &&
                          allConsumerKeys.every((k) => entityKeys.includes(k));
                        const allGeneratorsSelected =
                          allGeneratorKeys.length > 0 &&
                          allGeneratorKeys.every((k) => entityKeys.includes(k));
                        return (
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 2px)",
                              left: 0,
                              right: 0,
                              background: "#fff",
                              border: "1px solid #ccc",
                              borderRadius: 4,
                              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                              zIndex: 200,
                            }}
                          >
                            {/* Search input */}
                            <div
                              style={{
                                padding: "6px 8px",
                                borderBottom: "1px solid #eee",
                                position: "sticky",
                                top: 0,
                                background: "#fff",
                                zIndex: 1,
                              }}
                            >
                              <input
                                type="text"
                                placeholder="Search entity…"
                                value={entitySearch}
                                onChange={(e) =>
                                  setEntitySearch(e.target.value)
                                }
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  width: "100%",
                                  border: "1px solid #ddd",
                                  borderRadius: 4,
                                  padding: "4px 8px",
                                  fontSize: 12,
                                  outline: "none",
                                  boxSizing: "border-box",
                                }}
                              />
                            </div>
                            {/* Scrollable list */}
                            <div style={{ maxHeight: 180, overflowY: "auto" }}>
                              {filteredConsumers.length === 0 &&
                                filteredGenerators.length === 0 && (
                                  <div
                                    style={{
                                      padding: "10px",
                                      fontSize: 12,
                                      color: "#888",
                                    }}
                                  >
                                    No entities found
                                  </div>
                                )}
                              {/* Consumers group */}
                              {filteredConsumers.length > 0 && (
                                <>
                                  <div
                                    style={{
                                      padding: "5px 10px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      background: "#f5f5f5",
                                      borderBottom: "1px solid #eee",
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: "#34656d",
                                      textTransform: "uppercase",
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Consumers
                                    <span
                                      style={{
                                        cursor: "pointer",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        letterSpacing: 0,
                                        fontSize: 11,
                                        color: "#34656d",
                                        textDecoration: "underline",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (allConsumersSelected) {
                                          setEntityKeys((prev) =>
                                            prev.filter(
                                              (k) =>
                                                !allConsumerKeys.includes(k),
                                            ),
                                          );
                                        } else {
                                          setEntityKeys((prev) => {
                                            const existing = new Set(prev);
                                            return [
                                              ...prev,
                                              ...allConsumerKeys.filter(
                                                (k) => !existing.has(k),
                                              ),
                                            ];
                                          });
                                        }
                                      }}
                                    >
                                      {allConsumersSelected
                                        ? "Deselect All"
                                        : "Select All"}
                                    </span>
                                  </div>
                                  {filteredConsumers.map((c) => {
                                    const key = `C:${c.consumerId}`;
                                    const isSelected = entityKeys.includes(key);
                                    return (
                                      <div
                                        key={key}
                                        style={{
                                          padding: "7px 10px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          cursor: "pointer",
                                          background: isSelected
                                            ? "#e8f4f5"
                                            : "#fff",
                                          fontSize: 12,
                                        }}
                                        onClick={() => {
                                          setEntityKeys((prev) =>
                                            isSelected
                                              ? prev.filter((k) => k !== key)
                                              : [...prev, key],
                                          );
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          readOnly
                                          style={{ accentColor: "#34656d" }}
                                        />
                                        {c.consumerName || c.consumerId}
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                              {/* Generators group */}
                              {filteredGenerators.length > 0 && (
                                <>
                                  <div
                                    style={{
                                      padding: "5px 10px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      background: "#f5f5f5",
                                      borderTop:
                                        filteredConsumers.length > 0
                                          ? "1px solid #eee"
                                          : "none",
                                      borderBottom: "1px solid #eee",
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: "#c0572a",
                                      textTransform: "uppercase",
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Generators
                                    <span
                                      style={{
                                        cursor: "pointer",
                                        fontWeight: 600,
                                        textTransform: "none",
                                        letterSpacing: 0,
                                        fontSize: 11,
                                        color: "#c0572a",
                                        textDecoration: "underline",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (allGeneratorsSelected) {
                                          setEntityKeys((prev) =>
                                            prev.filter(
                                              (k) =>
                                                !allGeneratorKeys.includes(k),
                                            ),
                                          );
                                        } else {
                                          setEntityKeys((prev) => {
                                            const existing = new Set(prev);
                                            return [
                                              ...prev,
                                              ...allGeneratorKeys.filter(
                                                (k) => !existing.has(k),
                                              ),
                                            ];
                                          });
                                        }
                                      }}
                                    >
                                      {allGeneratorsSelected
                                        ? "Deselect All"
                                        : "Select All"}
                                    </span>
                                  </div>
                                  {filteredGenerators.map((g) => {
                                    const key = `G:${g.generatorId}`;
                                    const isSelected = entityKeys.includes(key);
                                    return (
                                      <div
                                        key={key}
                                        style={{
                                          padding: "7px 10px",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          cursor: "pointer",
                                          background: isSelected
                                            ? "#fff4ee"
                                            : "#fff",
                                          fontSize: 12,
                                        }}
                                        onClick={() => {
                                          setEntityKeys((prev) =>
                                            isSelected
                                              ? prev.filter((k) => k !== key)
                                              : [...prev, key],
                                          );
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          readOnly
                                          style={{ accentColor: "#c0572a" }}
                                        />
                                        {g.generatorName || g.generatorId}
                                      </div>
                                    );
                                  })}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            style={{ borderTop: "1px dashed #e8e8e8", margin: "0 0 16px" }}
          />

          {/* Section: Product & Configuration */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "#f1935c",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                2
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#f1935c" }}>
                Select Product &amp; Configuration
              </span>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                paddingLeft: 30,
              }}
            >
              <DataSelect
                label="Product"
                name="selProductId"
                value={selProductId}
                onChange={(e) => setSelProductId(e.target.value)}
                items={products}
                getValue={(p) => p.id}
                getLabel={(p) => `${p.productType} (#${p.id})`}
              />
              <DataSelect
                label="Payment Configuration"
                name="selConfigId"
                value={selConfigId}
                onChange={(e) => setSelConfigId(e.target.value)}
                items={configurations}
                getValue={(c) => c.id}
                getLabel={(c) => `${c.configurationName} (#${c.id})`}
              />
            </div>
          </div>

          {/* Add to List + Replace existing row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
              paddingLeft: 30,
            }}
          >
            <label
              htmlFor="replaceExisting"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                id="replaceExisting"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                style={{
                  accentColor: "#34656d",
                  width: 15,
                  height: 15,
                  cursor: "pointer",
                }}
              />
              <span className={Styles.fieldLabel} style={{ margin: 0 }}>
                Replace existing mappings
              </span>
            </label>
            <button
              className={Styles.btnPrimary}
              onClick={handleAddEntry}
              disabled={!canAdd}
              style={{ display: "flex", alignItems: "center", gap: 5 }}
            >
              <Add style={{ fontSize: 15 }} /> Add to List
            </button>
          </div>

          {/* Entries list */}
          {entries.length > 0 && (
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "8px 14px",
                  background: "#34656d",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Mappings to Submit</span>
                <span
                  style={{
                    background: "#fff",
                    color: "#34656d",
                    borderRadius: 100,
                    padding: "1px 8px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {entries.length}
                </span>
              </div>
              {entries.map((entry, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "9px 14px",
                    borderBottom:
                      i < entries.length - 1 ? "1px solid #f5f5f5" : "none",
                    background: i % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 13,
                    }}
                  >
                    <Chip
                      label={entry.entityType}
                      variant={
                        entry.entityType === "CONSUMER" ? "teal" : "orange"
                      }
                    />
                    <span style={{ color: "#444" }}>
                      Entity <strong>#{entry.entityId}</strong>
                    </span>
                    <span style={{ color: "#ccc", fontSize: 16 }}>›</span>
                    <span style={{ color: "#444" }}>
                      Company <strong>#{entry.companyId}</strong>
                    </span>
                    <span style={{ color: "#ccc", fontSize: 16 }}>›</span>
                    <span style={{ color: "#444" }}>
                      Config <strong>#{entry.configurationId}</strong>
                    </span>
                  </div>
                  <button
                    className={`${Styles.btnIcon} ${Styles.btnIconRed}`}
                    onClick={() => removeEntry(i)}
                    title="Remove"
                  >
                    <Delete fontSize="small" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </FormDialog>

        {/* Edit mapping dialog */}
        <FormDialog
          open={!!editRow}
          onClose={() => {
            setEditRow(null);
            setEditConfigId("");
          }}
          title="Edit Mapping"
          onSubmit={handleEditSubmit}
          submitLabel="Update"
        >
          {editRow && (
            <div className={Styles.formGrid}>
              <div className={Styles.field}>
                <label className={Styles.fieldLabel}>Entity</label>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    paddingTop: 6,
                  }}
                >
                  <Chip
                    label={editRow.entityType}
                    variant={
                      editRow.entityType === "CONSUMER" ? "teal" : "orange"
                    }
                  />
                  <span style={{ fontSize: 13, color: "#444" }}>
                    #{editRow.entityId}
                  </span>
                </div>
              </div>
              <DataSelect
                label="Payment Configuration"
                name="editConfigId"
                value={editConfigId}
                onChange={(e) => setEditConfigId(e.target.value)}
                items={configurations}
                getValue={(c) => c.id}
                getLabel={(c) => `${c.configurationName} (#${c.id})`}
              />
            </div>
          )}
        </FormDialog>

        {/* Existing mappings table */}
        <DataTable
          columns={[
            "Configuration",
            "Company Name",
            "Entity Name",
            "Entity",
            "Actions",
          ]}
          rows={currentMappings}
          renderRow={(row) => (
            <>
              <td className={Styles.td}>
                <div style={{ fontWeight: 500, color: "#333" }}>
                  {row.configurationName || `Config #${row.configurationId}`}
                </div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                  ID: {row.configurationId}
                </div>
              </td>
              <td className={Styles.td} style={{ fontWeight: 500, color: "#333" }}>
                {row.companyName}
              </td>
              <td className={Styles.td} style={{ fontWeight: 500, color: "#333" }}>
                {row.entityName}
              </td>
              <td className={Styles.td}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Chip
                    label={row.entityType}
                    variant={row.entityType === "CONSUMER" ? "teal" : "orange"}
                  />
                  <span style={{ fontSize: 12, color: "#999" }}>
                    #{row.entityId}
                  </span>
                </div>
              </td>
              <td className={Styles.td}>
                {/* <button
                className={`${Styles.btnIcon} ${Styles.btnIconTeal}`}
                onClick={() => openEdit(row)}
                title="Edit"
              >
                <Edit fontSize="small" />
              </button> */}
                <button
                  className={`${Styles.btnIcon} ${Styles.btnIconRed}`}
                  onClick={() => handleDeleteSingle(row)}
                  title="Remove"
                >
                  <Delete fontSize="small" />
                </button>
              </td>
            </>
          )}
        />
      </div>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ConfigurePlatform = ({ open, handleClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);

  const companies = useSelector(
    (state) => state?.gnaAdminSlice?.fetchCompany?.data,
  );
  const products = useSelector(selectProducts);
  const paymentConfigurations = useSelector(selectPaymentConfigurations);
  const paymentConfigRules = useSelector(selectPaymentConfigRules);
  const mappings = useSelector(selectMappings);
  const loading = useSelector(selectPlatformLoading);
  const wizard = useSelector(selectWizard);

  const fetchForTab = (tab) => {
    if (tab === 0) dispatch(fetchCompany());
    else if (tab === 1) dispatch(fetchAllProducts());
    else if (tab === 2) {
      dispatch(fetchAllPaymentConfigurations());
      dispatch(fetchAllPaymentConfigRules());
    }
  };

  useEffect(() => {
    if (open) fetchForTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleModalClose = () => {
    handleClose();
  };

  if (!open) return null;

  return (
    <div className={Styles.modalBackdrop} onClick={handleModalClose}>
      <div
        className={Styles.ConfigurePlatformMModal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={Styles.modalHeader}>
          <span className={Styles.modalTitle}>Configure Platform</span>
          <button className={Styles.modalClose} onClick={handleModalClose}>
            <Close fontSize="small" />
          </button>
        </div>

        {/* Wizard Context Bar */}
        <div className={Styles.wizardBar}>
          <span className={Styles.wizardLabel}>Chain:</span>
          <span
            className={`${Styles.wizardChip} ${wizard.productId ? Styles.wizardChipTeal : Styles.wizardChipInactive}`}
          >
            {wizard.productId ? `Product #${wizard.productId}` : "No Product"}
          </span>
          <span className={Styles.wizardArrow}>→</span>
          <span
            className={`${Styles.wizardChip} ${wizard.configurationId ? Styles.wizardChipOrange : Styles.wizardChipInactive}`}
          >
            {wizard.configurationId
              ? `Config #${wizard.configurationId}`
              : "No Config"}
          </span>
        </div>

        {/* Tabs */}
        <div className={Styles.tabBar}>
          {["Mappings", "Products", "Payment Configurations & Rules"].map(
            (label, i) => (
              <button
                key={label}
                className={`${Styles.tab} ${activeTab === i ? Styles.tabActive : ""}`}
                onClick={() => {
                  setActiveTab(i);
                  fetchForTab(i);
                }}
              >
                {label}
              </button>
            ),
          )}
        </div>

        {loading && <Loader />}

        {/* Tab Content */}
        <div className={Styles.tabContent}>
          {activeTab === 0 && (
            <MappingsTab
              allMappings={mappings}
              products={products}
              configurations={paymentConfigurations}
              companies={companies}
              rules={paymentConfigRules}
              dispatch={dispatch}
            />
          )}
          {activeTab === 1 && (
            <ProductsTab
              products={products}
              dispatch={dispatch}
              wizard={wizard}
            />
          )}
          {activeTab === 2 && (
            <>
              <PaymentConfigsTab
                configurations={paymentConfigurations}
                dispatch={dispatch}
                wizard={wizard}
              />
              <div
                style={{
                  margin: "24px 0 0",
                  borderTop: "1px solid #e0e0e0",
                  paddingTop: 20,
                }}
              >
                <PaymentConfigRulesTab
                  rules={paymentConfigRules}
                  configurations={paymentConfigurations}
                  dispatch={dispatch}
                  wizard={wizard}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurePlatform;
